/**
 * Chatbot Widget - Marea AI Shopping Assistant
 * Custom element that connects to the Vercel-hosted chatbot API via SSE streaming.
 * Follows Craft theme patterns (see cart-drawer.js, newsletter-popup.js).
 */

if (!customElements.get('chatbot-widget')) {
  class ChatbotWidget extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.messages = [];
      this.currentLang = this.dataset.locale || 'en';
      this.apiUrl = this.dataset.apiUrl || 'https://marea-chatbot.vercel.app';
      this.abortController = null;
      this.isStreaming = false;

      // Bottom sheet state (mobile only)
      this.isMobile = false;
      this.isDragging = false;
      this.dragStartY = 0;
      this.dragStartHeight = 0;
      this.currentHeight = 0;
      this.maxHeight = 0;
      this.contentHeight = 0;
      this.resizeObserver = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    connectedCallback() {
      this.bubble = this.querySelector('.chatbot-bubble');
      this.container = this.querySelector('.chatbot-container');
      this.panel = this.querySelector('.chatbot-panel');
      this.overlay = this.querySelector('.chatbot-overlay');
      this.messagesEl = this.querySelector('.chatbot-messages');
      this.suggestionsEl = this.querySelector('.chatbot-suggestions');
      this.textarea = this.querySelector('.chatbot-input__field');
      this.sendBtn = this.querySelector('.chatbot-input__send');
      this.form = this.querySelector('.chatbot-input__form');
      this.closeBtn = this.querySelector('.chatbot-close-btn');
      this.langToggle = this.querySelector('.chatbot-lang-toggle');

      this.dragHandle = this.querySelector('.chatbot-drag-handle');
      this.header = this.querySelector('.chatbot-header');

      this.bindEvents();
      if (!this.restoreSession()) {
        this.showGreeting();
      }
      this.setupBottomSheet();
    }

    disconnectedCallback() {
      this.cancelStream();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    // -----------------------------------------------------------------------
    // Event Binding
    // -----------------------------------------------------------------------

    bindEvents() {
      this.bubble.addEventListener('click', () => this.toggle());
      this.closeBtn.addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', () => this.close());
      this.langToggle.addEventListener('click', () => this.toggleLanguage());

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const text = this.textarea.value.trim();
        if (text && !this.isStreaming) this.sendMessage(text);
      });

      this.textarea.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          const text = this.textarea.value.trim();
          if (text && !this.isStreaming) this.sendMessage(text);
        }
      });

      this.textarea.addEventListener('input', () => this.autoResize());

      this.suggestionsEl.addEventListener('click', (e) => {
        const btn = e.target.closest('.chatbot-suggestion');
        if (btn && !this.isStreaming) {
          this.sendMessage(btn.dataset.message);
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });
    }

    // -----------------------------------------------------------------------
    // Open / Close
    // -----------------------------------------------------------------------

    toggle() {
      this.isOpen ? this.close() : this.open();
    }

    open() {
      this.isOpen = true;
      this.isMobile = window.innerWidth < 750;
      this.container.dataset.open = 'true';
      this.container.setAttribute('aria-hidden', 'false');
      this.container.dataset.closing = 'false';
      this.bubble.setAttribute('aria-expanded', 'true');

      // Prevent body scroll on mobile
      if (this.isMobile) {
        document.body.style.overflow = 'hidden';

        // Initialize bottom sheet sizing
        this.maxHeight = this.calculateMaxHeight();

        // Double rAF to ensure DOM is laid out after display:none → block
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.contentHeight = this.calculateContentHeight();
            const initialH = Math.max(200, Math.min(this.contentHeight, this.maxHeight));
            this.setPanelHeight(initialH, false);

            // Start observing content growth
            this.resizeObserver.observe(this.messagesEl);
          });
        });
      }

      // Focus trap using theme's trapFocus if available
      if (typeof trapFocus === 'function') {
        trapFocus(this.panel, this.textarea);
      } else {
        this.textarea.focus();
      }

      this.scrollToBottom();

      // GA4 event
      this.trackEvent('chatbot_opened');
    }

    close() {
      if (!this.isOpen) return;

      this.container.dataset.closing = 'true';

      const onAnimationEnd = () => {
        this.container.dataset.open = 'false';
        this.container.setAttribute('aria-hidden', 'true');
        this.container.dataset.closing = 'false';
        this.isOpen = false;
        this.bubble.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';

        // Clean up bottom sheet state
        if (this.resizeObserver) this.resizeObserver.disconnect();
        this.panel.style.removeProperty('--chatbot-panel-h');
        this.panel.classList.remove('chatbot-panel--transitioning', 'chatbot-panel--dragging');
        this.currentHeight = 0;

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.bubble);
        } else {
          this.bubble.focus();
        }

        this.panel.removeEventListener('animationend', onAnimationEnd);
      };

      this.panel.addEventListener('animationend', onAnimationEnd);

      // Fallback if animationend doesn't fire (reduced motion)
      setTimeout(() => {
        if (this.container.dataset.closing === 'true') onAnimationEnd();
      }, 300);
    }

    // -----------------------------------------------------------------------
    // Bottom Sheet (Mobile)
    // -----------------------------------------------------------------------

    setupBottomSheet() {
      this.isMobile = window.innerWidth < 750;

      // Drag events on the drag handle (touch-action: none in CSS)
      this.dragHandle.addEventListener('touchstart', (e) => this.onDragStart(e), { passive: true });
      this.dragHandle.addEventListener('touchmove', (e) => this.onDragMove(e), { passive: false });
      this.dragHandle.addEventListener('touchend', (e) => this.onDragEnd(e), { passive: true });

      // Also allow dragging from the header (larger touch target)
      // but don't set touch-action: none on header (buttons need default behavior)
      this.header.addEventListener('touchstart', (e) => {
        // Only start drag if the touch is not on a button
        if (e.target.closest('button')) return;
        this.onDragStart(e);
      }, { passive: true });
      this.header.addEventListener('touchmove', (e) => {
        if (!this.isDragging) return;
        this.onDragMove(e);
      }, { passive: false });
      this.header.addEventListener('touchend', (e) => {
        if (!this.isDragging) return;
        this.onDragEnd(e);
      }, { passive: true });

      // ResizeObserver for content growth detection
      this.resizeObserver = new ResizeObserver(() => {
        if (this.isOpen && this.isMobile && !this.isDragging) {
          this.updatePanelHeight();
        }
      });

      // Recalculate on window resize (e.g. iOS keyboard open/close)
      window.addEventListener('resize', () => {
        this.isMobile = window.innerWidth < 750;
        if (this.isOpen && this.isMobile) {
          this.maxHeight = this.calculateMaxHeight();
        }
      });
    }

    calculateContentHeight() {
      const dragH = this.dragHandle ? this.dragHandle.offsetHeight : 0;
      const headerH = this.header ? this.header.offsetHeight : 0;
      // Sum actual children instead of scrollHeight to avoid flex feedback loop
      let messagesH = 0;
      if (this.messagesEl) {
        for (const child of this.messagesEl.children) {
          messagesH += child.offsetHeight;
        }
      }
      const suggestionsH = this.suggestionsEl ? this.suggestionsEl.offsetHeight : 0;
      const inputH = this.querySelector('.chatbot-input')?.offsetHeight || 0;
      return dragH + headerH + messagesH + suggestionsH + inputH + 16;
    }

    calculateMaxHeight() {
      return window.innerHeight - 64;
    }

    updatePanelHeight() {
      if (!this.isMobile || !this.isOpen) return;

      this.contentHeight = this.calculateContentHeight();
      const minH = 200;
      const target = Math.max(minH, Math.min(this.contentHeight, this.maxHeight));

      // Only expand, never shrink (shrinking mid-conversation is jarring)
      if (target > this.currentHeight) {
        this.setPanelHeight(target, true);
      }
    }

    setPanelHeight(height, animate) {
      this.currentHeight = height;
      this.panel.style.setProperty('--chatbot-panel-h', height + 'px');

      if (animate && !this.reducedMotion) {
        this.panel.classList.add('chatbot-panel--transitioning');
        const onEnd = () => {
          this.panel.classList.remove('chatbot-panel--transitioning');
          this.panel.removeEventListener('transitionend', onEnd);
        };
        this.panel.addEventListener('transitionend', onEnd);
        // Fallback if transitionend doesn't fire
        setTimeout(onEnd, 350);
      }
    }

    onDragStart(e) {
      if (!this.isMobile || !this.isOpen) return;

      const touch = e.touches[0];
      this.isDragging = true;
      this.dragStartY = touch.clientY;
      this.dragStartHeight = this.currentHeight || this.panel.offsetHeight;
      this.dragStartTime = Date.now();

      this.panel.classList.add('chatbot-panel--dragging');
      this.panel.classList.remove('chatbot-panel--transitioning');
    }

    onDragMove(e) {
      if (!this.isDragging) return;
      e.preventDefault(); // Prevent iOS page scroll

      const touch = e.touches[0];
      const delta = this.dragStartY - touch.clientY; // positive = dragging up
      let newHeight = this.dragStartHeight + delta;

      // Rubber-band resistance beyond bounds
      const minH = 200;
      if (newHeight > this.maxHeight) {
        const overflow = newHeight - this.maxHeight;
        newHeight = this.maxHeight + overflow / 4;
      } else if (newHeight < minH) {
        const underflow = minH - newHeight;
        newHeight = minH - underflow / 4;
      }

      requestAnimationFrame(() => {
        this.panel.style.setProperty('--chatbot-panel-h', newHeight + 'px');
      });

      this.lastDragY = touch.clientY;
    }

    onDragEnd(e) {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.panel.classList.remove('chatbot-panel--dragging');

      const touch = e.changedTouches[0];
      const totalDelta = this.dragStartY - touch.clientY; // negative = dragged down
      const elapsed = Date.now() - this.dragStartTime;
      const velocity = totalDelta / Math.max(elapsed, 1); // px/ms

      const currentH = this.dragStartHeight + totalDelta;
      const viewportThreshold = window.innerHeight * 0.3;

      // Quick swipe down (>0.5 px/ms downward) or dragged below 30% viewport
      if (velocity < -0.5 || currentH < viewportThreshold) {
        this.close();
        return;
      }

      // Snap logic: above midpoint between content and max → snap to max
      const midpoint = (this.contentHeight + this.maxHeight) / 2;
      if (currentH > midpoint) {
        this.setPanelHeight(this.maxHeight, true);
      } else {
        // Snap to content height
        const target = Math.max(200, Math.min(this.contentHeight, this.maxHeight));
        this.setPanelHeight(target, true);
      }
    }

    // -----------------------------------------------------------------------
    // Messaging
    // -----------------------------------------------------------------------

    sendMessage(text) {
      // Add user message to DOM (uses textContent, safe against XSS)
      this.addMessage(text, 'user');
      this.textarea.value = '';
      this.textarea.style.height = 'auto';
      this.hideSuggestions();

      // Track context (last 10 messages)
      this.messages.push({ role: 'user', content: text });
      if (this.messages.length > 20) this.messages = this.messages.slice(-20);
      this.saveSession();

      // Show typing indicator
      this.showTypingIndicator();
      this.isStreaming = true;
      this.sendBtn.disabled = true;

      // GA4 event
      this.trackEvent('chatbot_message_sent');

      this.fetchStream(text);
    }

    async fetchStream(text) {
      this.cancelStream();
      this.abortController = new AbortController();

      try {
        const response = await fetch(`${this.apiUrl}/api/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: text,
            language: this.currentLang,
            history: this.messages.slice(-10),
          }),
          signal: this.abortController.signal,
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        this.removeTypingIndicator();
        await this.processStream(response);
      } catch (err) {
        this.removeTypingIndicator();
        if (err.name !== 'AbortError') {
          this.addMessage(this.getTranslation('error'), 'assistant');
        }
      } finally {
        this.isStreaming = false;
        this.sendBtn.disabled = false;
      }
    }

    async processStream(response) {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';
      let messageBubble = null;
      let statusEl = null;

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // Keep incomplete line in buffer

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const dataStr = line.slice(6).trim();
            if (dataStr === '[DONE]') continue;

            try {
              const data = JSON.parse(dataStr);

              // Handle status events (e.g. "Searching...")
              if (data.type === 'status' || data.status) {
                if (!messageBubble) {
                  messageBubble = this.createMessageBubble('assistant');
                }
                if (!statusEl) {
                  statusEl = document.createElement('div');
                  statusEl.className = 'chatbot-message__status';
                  messageBubble.prepend(statusEl);
                }
                statusEl.textContent = data.status || data.text || '';
                this.scrollToBottom();
                continue;
              }

              // Handle text content
              if (data.text) {
                // Remove status indicator once text starts flowing
                if (statusEl) {
                  statusEl.remove();
                  statusEl = null;
                }

                if (!messageBubble) {
                  messageBubble = this.createMessageBubble('assistant');
                }

                fullText += data.text;
                // Safety: renderMarkdown first escapes ALL HTML via textContent,
                // then applies a strict allowlist of markdown patterns (bold, italic,
                // links with https-only URLs, line breaks). No raw HTML passes through.
                this.renderMarkdown(messageBubble, fullText);
                this.scrollToBottom();
              }
            } catch {
              // Non-JSON data line, skip
            }
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') throw err;
      }

      // Store assistant message in history, or show error if stream returned no text
      if (fullText) {
        this.messages.push({ role: 'assistant', content: fullText });
        this.saveSession();
      } else {
        // Stream completed but API returned no text (e.g. search pipeline failure)
        if (messageBubble) messageBubble.remove();
        this.addMessage(this.getTranslation('error'), 'assistant');
      }
    }

    cancelStream() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    // -----------------------------------------------------------------------
    // DOM Helpers
    // -----------------------------------------------------------------------

    addMessage(text, role) {
      const bubble = this.createMessageBubble(role);
      if (role === 'user') {
        // User messages are plain text - use safe textContent
        bubble.textContent = text;
      } else {
        // Assistant messages may contain markdown formatting
        this.renderMarkdown(bubble, text);
      }
      this.scrollToBottom();

      // Update bottom sheet height on mobile after new content
      if (this.isMobile && this.isOpen) {
        requestAnimationFrame(() => this.updatePanelHeight());
      }

      return bubble;
    }

    createMessageBubble(role) {
      const el = document.createElement('div');
      el.className = `chatbot-message chatbot-message--${role}`;
      if (role === 'assistant' && this.currentLang === 'ar') {
        el.dir = 'rtl';
      }
      this.messagesEl.appendChild(el);
      return el;
    }

    showTypingIndicator() {
      const el = document.createElement('div');
      el.className = 'chatbot-typing';
      el.id = 'chatbot-typing';
      for (let i = 0; i < 3; i++) {
        const dot = document.createElement('span');
        dot.className = 'chatbot-typing__dot';
        el.appendChild(dot);
      }
      this.messagesEl.appendChild(el);
      this.scrollToBottom();
    }

    removeTypingIndicator() {
      const el = this.messagesEl.querySelector('#chatbot-typing');
      if (el) el.remove();
    }

    hideSuggestions() {
      while (this.suggestionsEl.firstChild) {
        this.suggestionsEl.removeChild(this.suggestionsEl.firstChild);
      }
    }

    scrollToBottom() {
      requestAnimationFrame(() => {
        this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
      });
    }

    autoResize() {
      this.textarea.style.height = 'auto';
      this.textarea.style.height = Math.min(this.textarea.scrollHeight, 100) + 'px';
    }

    // -----------------------------------------------------------------------
    // Language Toggle
    // -----------------------------------------------------------------------

    toggleLanguage() {
      this.currentLang = this.currentLang === 'en' ? 'ar' : 'en';
      const label = this.langToggle.querySelector('.chatbot-header__lang-label');
      label.textContent = this.currentLang === 'en' ? '\u0639\u0631\u0628\u064A' : 'EN';

      // Reset conversation
      this.clearSession();
      this.messages = [];
      while (this.messagesEl.firstChild) {
        this.messagesEl.removeChild(this.messagesEl.firstChild);
      }
      this.cancelStream();
      this.isStreaming = false;
      this.sendBtn.disabled = false;

      this.showGreeting();
      this.restoreSuggestions();
    }

    showGreeting() {
      const greeting = this.getTranslation('greeting');
      this.addMessage(greeting, 'assistant');
    }

    restoreSuggestions() {
      const suggestions = [
        this.getTranslation('suggestion_1'),
        this.getTranslation('suggestion_2'),
        this.getTranslation('suggestion_3'),
      ];
      // Clear existing suggestions safely
      while (this.suggestionsEl.firstChild) {
        this.suggestionsEl.removeChild(this.suggestionsEl.firstChild);
      }
      // Build suggestion buttons using DOM methods
      suggestions.forEach((text) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'chatbot-suggestion';
        btn.dataset.message = text;
        btn.textContent = text;
        this.suggestionsEl.appendChild(btn);
      });
    }

    // -----------------------------------------------------------------------
    // Translations (client-side fallback for language toggle)
    // -----------------------------------------------------------------------

    getTranslation(key) {
      const translations = {
        en: {
          greeting: 'Hello! I\'m your Marea shopping assistant. How can I help you today?',
          error: 'Sorry, something went wrong. Please try again.',
          suggestion_1: 'Ring sizes',
          suggestion_2: 'Shipping info',
          suggestion_3: 'Jewelry care',
        },
        ar: {
          greeting: '\u0645\u0631\u062D\u0628\u0627\u064B! \u0623\u0646\u0627 \u0645\u0633\u0627\u0639\u062F\u0629 \u0627\u0644\u062A\u0633\u0648\u0642 \u0641\u064A \u0645\u0627\u0631\u064A\u0627. \u0643\u064A\u0641 \u064A\u0645\u0643\u0646\u0646\u064A \u0645\u0633\u0627\u0639\u062F\u062A\u0643 \u0627\u0644\u064A\u0648\u0645\u061F',
          error: '\u0639\u0630\u0631\u0627\u064B\u060C \u062D\u062F\u062B \u062E\u0637\u0623 \u0645\u0627. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.',
          suggestion_1: '\u0645\u0642\u0627\u0633\u0627\u062A \u0627\u0644\u062E\u0648\u0627\u062A\u0645',
          suggestion_2: '\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0634\u062D\u0646',
          suggestion_3: '\u0627\u0644\u0639\u0646\u0627\u064A\u0629 \u0628\u0627\u0644\u0645\u062C\u0648\u0647\u0631\u0627\u062A',
        },
      };
      return translations[this.currentLang]?.[key] || translations.en[key] || key;
    }

    // -----------------------------------------------------------------------
    // Markdown Renderer (safe: escapes all HTML first, then applies formatting)
    // -----------------------------------------------------------------------

    /**
     * Renders markdown-formatted text into a DOM element safely.
     * 1. Escapes ALL HTML entities via textContent (prevents XSS)
     * 2. Applies a strict allowlist of markdown patterns:
     *    - Bold: **text**
     *    - Italic: *text*
     *    - Links: [text](https://...) (https-only URLs)
     *    - Line breaks: \n
     */
    renderMarkdown(container, text) {
      // Step 1: Escape all HTML entities using safe DOM method
      const escapeDiv = document.createElement('div');
      escapeDiv.textContent = text;
      let safeHtml = escapeDiv.textContent;

      // Step 2: Convert to HTML entities for safe string manipulation
      safeHtml = safeHtml
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

      // Step 3: Apply markdown patterns on the escaped string
      // Bold: **text**
      safeHtml = safeHtml.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      // Italic: *text* (not preceded/followed by another *)
      safeHtml = safeHtml.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
      // Links: [text](https://url) - only allow https URLs
      safeHtml = safeHtml.replace(
        /\[([^\]]+)\]\((https:\/\/[^\s)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
      );
      // Line breaks
      safeHtml = safeHtml.replace(/\n/g, '<br>');

      // Step 4: Set via a template element for safe DOM parsing
      const template = document.createElement('template');
      template.innerHTML = safeHtml.trim();
      while (container.firstChild) container.removeChild(container.firstChild);
      container.appendChild(template.content.cloneNode(true));
    }

    // -----------------------------------------------------------------------
    // Utilities
    // -----------------------------------------------------------------------

    trackEvent(name) {
      if (typeof gtag === 'function') {
        gtag('event', name, { event_category: 'chatbot' });
      }
    }

    // -----------------------------------------------------------------------
    // Session Persistence
    // -----------------------------------------------------------------------

    saveSession() {
      try {
        sessionStorage.setItem(
          'marea_chatbot_session',
          JSON.stringify({ messages: this.messages, lang: this.currentLang })
        );
      } catch {
        // Private browsing or storage full — silently degrade
      }
    }

    restoreSession() {
      try {
        const raw = sessionStorage.getItem('marea_chatbot_session');
        if (!raw) return false;

        const state = JSON.parse(raw);
        if (!state.messages || !state.messages.length) return false;

        // Restore language if it differs from the page locale
        if (state.lang && state.lang !== this.currentLang) {
          this.currentLang = state.lang;
          const label = this.langToggle.querySelector('.chatbot-header__lang-label');
          if (label) label.textContent = this.currentLang === 'en' ? '\u0639\u0631\u0628\u064A' : 'EN';
        }

        // Show greeting in the restored language (greeting is UI-only, not in messages)
        this.showGreeting();

        // Replay stored messages into the DOM
        state.messages.forEach((msg) => this.addMessage(msg.content, msg.role));

        // Hide suggestions since the user already interacted
        this.hideSuggestions();

        // Restore API history for follow-up questions
        this.messages = state.messages;

        return true;
      } catch {
        return false;
      }
    }

    clearSession() {
      try {
        sessionStorage.removeItem('marea_chatbot_session');
      } catch {
        // Ignore storage errors
      }
    }
  }

  customElements.define('chatbot-widget', ChatbotWidget);
}
